/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import type { ReactNode } from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { theme } from "../styles/theme/theme";
import { useRunSelection, type RunRow } from "./runSelectionContext";
import {
  findRecommendedRun,
  formatRunAvailability,
  formatRunOptionLabel,
  type ReceiptValidity,
  type ReceiptValidityByRunId,
} from "./runSelectionState";

const s = {
  trigger: css`
    height: auto;
    padding: 0.125rem 0.25rem;
    font-size: 0.75rem;
    border: none;
    border-radius: 0.25rem;
    min-width: 0;
    width: auto;
    background: transparent;
    box-shadow: none !important;
    color: ${theme.colors.mutedForeground};
    gap: 0.25rem;
    &:hover {
      color: ${theme.colors.foreground};
      background: ${theme.colors.gray100};
    }
    &:focus-visible {
      box-shadow: none !important;
      border: none;
      outline: 2px solid ${theme.colors.ring};
      outline-offset: 2px;
    }
    &[data-state="open"] {
      color: ${theme.colors.foreground};
      background: ${theme.colors.gray100};
    }
  `,
  content: css`
    max-height: 20rem;
  `,
  item: css`
    font-size: ${theme.typography.fontSize.xs};
  `,
  groupLabel: css`
    font-size: ${theme.typography.fontSize.xs};
  `,
};

export interface UseRunSelectorResult {
  selectedRun: RunRow | null;
  selectedRunId: string | null;
  setSelectedRunId: (id: string | null) => void;
  sortedRuns: RunRow[];
  visibleRuns: RunRow[];
  receiptValidityByRunId: ReceiptValidityByRunId;
  reportReceiptValidity: (runId: string, validity: Exclude<ReceiptValidity, "unknown">) => void;
  loadingRuns: boolean;
  /** Canonical runSettings JSON string for the selected run (pass to useViewTransform / useSchema). Empty string if no run selected. */
  runSettingsJson: string;
  /** Ready-made ReactNode to pass as PageHeader's titleAddon */
  titleAddon: ReactNode;
}

export function useRunSelector(): UseRunSelectorResult {
  const {
    loadingRuns,
    receiptValidityByRunId,
    reportReceiptValidity,
    runSettingsJson,
    selectedRun,
    selectedRunId,
    setSelectedRunId,
    sortedRuns,
    visibleRuns,
  } = useRunSelection();
  const recommendedRun = findRecommendedRun(sortedRuns, receiptValidityByRunId);
  const otherCompletedRuns = sortedRuns.filter((run) => run.id !== recommendedRun?.id);
  const unavailableRuns = visibleRuns.filter((run) => run.status !== "RUN_COMPLETED");
  const emptySelectionLabel = loadingRuns
    ? "Loading…"
    : visibleRuns.length > 0
      ? "No completed runs"
      : "No runs";

  const itemLabel = (run: RunRow): string => {
    const validity = receiptValidityByRunId[run.id] ?? "unknown";
    const label = formatRunOptionLabel(run);
    return run.id === recommendedRun?.id
      ? `Recommended · ${label}`
      : `${label} · ${formatRunAvailability(run.status, validity)}`;
  };

  const titleAddon: ReactNode = (
    <Select value={selectedRunId ?? ""} onValueChange={setSelectedRunId}>
      <SelectTrigger size="sm" css={s.trigger} aria-label="Planning run">
        <SelectValue placeholder={emptySelectionLabel} />
      </SelectTrigger>
      <SelectContent css={s.content}>
        {recommendedRun && (
          <SelectGroup>
            <SelectLabel css={s.groupLabel}>Newest validated receipt</SelectLabel>
            <SelectItem key={recommendedRun.id} value={recommendedRun.id} css={s.item}>
              {itemLabel(recommendedRun)}
            </SelectItem>
          </SelectGroup>
        )}
        {recommendedRun && (otherCompletedRuns.length > 0 || unavailableRuns.length > 0) && (
          <SelectSeparator />
        )}
        {otherCompletedRuns.length > 0 && (
          <SelectGroup>
            <SelectLabel css={s.groupLabel}>
              {recommendedRun ? "Other completed runs" : "Completed runs"} · newest first
            </SelectLabel>
            {otherCompletedRuns.map((run) => (
              <SelectItem key={run.id} value={run.id} css={s.item}>
                {itemLabel(run)}
              </SelectItem>
            ))}
          </SelectGroup>
        )}
        {otherCompletedRuns.length > 0 && unavailableRuns.length > 0 && <SelectSeparator />}
        {unavailableRuns.length > 0 && (
          <SelectGroup>
            <SelectLabel css={s.groupLabel}>Unavailable runs · newest first</SelectLabel>
            {unavailableRuns.map((run) => (
              <SelectItem key={run.id} value={run.id} css={s.item} disabled>
                {itemLabel(run)}
              </SelectItem>
            ))}
          </SelectGroup>
        )}
      </SelectContent>
    </Select>
  );

  return {
    selectedRun,
    selectedRunId,
    setSelectedRunId,
    sortedRuns,
    visibleRuns,
    receiptValidityByRunId,
    reportReceiptValidity,
    loadingRuns,
    runSettingsJson,
    titleAddon,
  };
}
